import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, CheckCheck, Package, Truck, CheckCircle, Clock,
  Loader2, ArrowLeft, Sparkles, Star
} from 'lucide-react';
import IndividualBottomNav from '@/components/IndividualBottomNav';
import IndividualSidebar from '@/components/IndividualSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_CONFIG = {
  order_placed:         { icon: Package,      color: 'bg-blue-100 text-blue-600' },
  pickup_confirmed:     { icon: Clock,        color: 'bg-amber-100 text-amber-600' },
  order_pickup_done:    { icon: CheckCircle,  color: 'bg-blue-100 text-blue-600' },
  order_in_process:     { icon: Sparkles,     color: 'bg-violet-100 text-violet-600' },
  order_process_complete:{ icon: CheckCheck,  color: 'bg-teal-100 text-teal-600' },
  order_cleaned:        { icon: Star,         color: 'bg-emerald-100 text-emerald-600' },
  order_out_for_delivery:{ icon: Truck,       color: 'bg-orange-100 text-orange-600' },
  order_delivered:      { icon: CheckCircle,  color: 'bg-emerald-100 text-emerald-600' },
  referral_reward_earned:{ icon: Star,        color: 'bg-amber-100 text-amber-600' },
  referral_reward_applied:{ icon: CheckCheck, color: 'bg-violet-100 text-violet-600' },
  default:              { icon: Bell,         color: 'bg-slate-100 text-slate-500' },
};

const getConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.default;

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const IndividualNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [markingAll, setMarkingAll]       = useState(false);

  const token = localStorage.getItem('individualToken');

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/individual/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data || []);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate('/individual/login'); return; }
    fetchNotifications();
  }, [fetchNotifications, navigate, token]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/individual/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { /* non-critical */ }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.post(`${API}/individual/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
              <Bell className="w-4 h-4 text-blue-600" />
            </div>
            <h1 className="font-black text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Notifications</h1>
          </div>
          {unreadCount > 0 && (
            <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-4">

            {/* Desktop heading */}
            <div className="hidden md:flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Notifications</h1>
                <p className="text-slate-400 text-sm mt-0.5">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                </p>
              </div>
              {unreadCount > 0 && (
                <button onClick={markAllRead} disabled={markingAll}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-semibold transition-all disabled:opacity-50">
                  {markingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                  Mark all read
                </button>
              )}
            </div>

            {/* Mobile mark all */}
            {unreadCount > 0 && (
              <button onClick={markAllRead} disabled={markingAll}
                className="md:hidden w-full flex items-center justify-center gap-1.5 py-2.5 rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold transition-all disabled:opacity-50">
                {markingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                Mark all as read
              </button>
            )}

            {/* List */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-[2rem] py-16 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                  <Bell className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
                </div>
                <p className="font-semibold text-slate-500 mb-1">No notifications yet</p>
                <p className="text-sm text-slate-400">You'll be notified on every order update</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((n) => {
                  const cfg = getConfig(n.type);
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={n.id}
                      onClick={() => { if (!n.is_read) markRead(n.id); }}
                      className={`flex items-start gap-3 px-4 py-4 rounded-[1.5rem] border cursor-pointer transition-all ${
                        n.is_read
                          ? 'bg-white border-slate-100 opacity-70'
                          : 'bg-white border-blue-100 shadow-sm shadow-blue-50'
                      }`}
                    >
                      <div className={`w-10 h-10 ${cfg.color} rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-bold ${n.is_read ? 'text-slate-500' : 'text-slate-800'}`}>
                            {n.title}
                          </p>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                            {timeAgo(n.created_at)}
                          </span>
                        </div>
                        <p className={`text-xs mt-0.5 leading-relaxed ${n.is_read ? 'text-slate-400' : 'text-slate-600'}`}>
                          {n.message}
                        </p>
                      </div>
                      {!n.is_read && (
                        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
      <IndividualBottomNav />
    </div>
  );
};

export default IndividualNotifications;
